import type {InputBaseProps as MaterialInputProps} from '@mui/material/InputBase';
import MuiInputBase from '@mui/material/InputBase';
import {styled} from '@mui/material/styles';
import React from 'react';
import {FuelInputVariant, getFuelInputVariant, getFuelSize} from '../types/general';
import {getInputTokens} from './inputTokens';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface InputProps extends Omit<MaterialInputProps, 'color'> {
    variant?: FuelInputVariant;
}

const StyledInput = styled(MuiInputBase, {
    ...excludeComponentProps('variant', 'size')
})<InputProps>(({
    theme, size: currentSize, variant: currentVariant, readOnly
}) => {
    const size = getFuelSize(currentSize);
    const variant = getFuelInputVariant(currentVariant);
    const inputTokens = getInputTokens(theme);
    const tokenKey = `${variant}-${size}`;

    return {
        borderRadius: theme.tokens.FuelBorderRadiusMd,
        position: 'relative',
        fontSize: inputTokens[tokenKey].fontSize,
        boxSizing: 'border-box',
        WebkitAppearance: 'none',
        transition: 'none',

        // for contents inside
        display: 'flex',
        alignItems: 'flex-start',

        /** Variant */
        border: inputTokens[tokenKey].border,
        boxShadow: inputTokens[tokenKey].boxShadow,
        backgroundColor: inputTokens[tokenKey].backgroundColor,
        minHeight: inputTokens[tokenKey].containerHeight,
        padding: `calc(${inputTokens[tokenKey].containerPadding} - ${inputTokens[tokenKey].borderWidth}) 0`,

        '&.Mui-focused': {
            outline: inputTokens[tokenKey].focused.outline,
            outlineOffset: inputTokens[tokenKey].focused.outlineOffset,
            borderColor: inputTokens[tokenKey].focused.borderColor
        },

        '& .MuiInputBase-input': {
            padding: `0 calc(${inputTokens[tokenKey].inputPadding} - ${inputTokens[tokenKey].borderWidth})`,
            minHeight: inputTokens[tokenKey].inputHeight,
            lineHeight: inputTokens[tokenKey].inputHeight
        },

        '&.MuiInputBase-adornedStart, &.MuiInputBase-adornedEnd': {

            '& .MuiInputAdornment-positionStart, & .MuiInputAdornment-positionEnd': {
                height: inputTokens[tokenKey].inputHeight,
                maxHeight: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,

                '& > .MuiIconButton-root': {
                    height: '1.5rem',
                    width: '1.5rem'
                },

                '& > .MuiSvgIcon-root, & > .MuiIconButton-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fill: `${theme.tokens.FuelColorGray700}`
                }
            }
        },

        // States

        '&::placeholder': {
            color: theme.tokens.FuelColorTextTertiary,
            opacity: '1'
        },
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            backgroundColor: theme.tokens.FuelColorGray100,
            borderColor: theme.tokens.FuelColorBorderLight
        },

        ...readOnly && {
            backgroundColor: theme.tokens.FuelColorGray100
        },

        '&.Mui-error': {
            border: `1px solid ${theme.palette.error.main}`,

            '&.Mui-focused': {
                boxShadow: `${theme.palette.error.main} 0px 0px 0px 1px`,
                borderColor: theme.palette.error.main
            }
        }
    };
});

export const Input = React.forwardRef<HTMLDivElement, InputProps>(({
    children, variant = 'outlined', ...props
}, ref) => (
    <StyledInput
        {...props}
        color={undefined}
        ref={ref}
        variant={variant}
    >{children}</StyledInput>
));

Input.displayName = 'Input';
